import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BuildingOffice2Icon, ChevronDownIcon } from "@heroicons/react/24/outline";

import { APIService } from "../services/api-service";
import { getAccessToken } from "../lib/general/token";

export type Location = { id: string; name: string; code: string; addressId?: string | null };

type LocationContextValue = {
  locations: Location[];
  activeLocationId: string | null;
  canViewAll: boolean;
  setActiveLocation: (locationId: string) => void;
};

const LocationContext = createContext<LocationContextValue | null>(null);
const STORAGE_KEY = "tig.active-location-id";

export function LocationProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const hasToken = Boolean(getAccessToken());
  const { data, isLoading } = useQuery({
    queryKey: ["location-scope"],
    enabled: hasToken,
    queryFn: async () => (await APIService.get<{ items: Location[]; canViewAll: boolean }>("/locations/me", { withAuthToken: true })).data,
  });
  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    const validSaved = saved === "all"
      ? data.canViewAll
      : data.items.some((location) => location.id === saved);
    const selected = validSaved ? saved : (data.canViewAll ? "all" : data.items[0]?.id ?? null);
    setActiveLocationId(selected);
    APIService.setActiveLocation(selected);
  }, [data]);

  const value = useMemo<LocationContextValue>(() => ({
    locations: data?.items ?? [],
    activeLocationId,
    canViewAll: data?.canViewAll ?? false,
    setActiveLocation: (locationId: string) => {
      APIService.setActiveLocation(locationId);
      window.localStorage.setItem(STORAGE_KEY, locationId);
      setActiveLocationId(locationId);
      // Existing screens do not all have branch-aware React Query keys. By
      // invalidating them, their ordinary API calls refetch under this header.
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] !== "location-scope",
      });
    },
  }), [activeLocationId, data, queryClient]);

  // Do not let authenticated CRM views issue a request before their branch
  // header exists. Login/public views continue rendering normally.
  if (hasToken && (isLoading || (data && !activeLocationId))) return null;
  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useLocationScope() {
  const context = useContext(LocationContext);
  if (!context) throw new Error("useLocationScope must be used inside LocationProvider");
  return context;
}

export function LocationSwitcher() {
  const { locations, activeLocationId, canViewAll, setActiveLocation } = useLocationScope();
  if (!locations.length) return null;
  const activeLocation = activeLocationId === "all"
    ? "All branches"
    : locations.find((location) => location.id === activeLocationId)?.name ?? "Select branch";

  return (
    <label className="group relative flex min-w-0 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 py-1.5 pl-2.5 pr-2 text-left shadow-sm transition-colors hover:border-indigo-200 hover:bg-indigo-50/40 focus-within:border-indigo-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-indigo-100">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
        <BuildingOffice2Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <span className="hidden min-w-0 md:block">
        <span className="block text-[10px] font-semibold uppercase leading-3 tracking-[0.08em] text-slate-500">Viewing</span>
        <span className="block max-w-32 truncate text-sm font-semibold leading-4 text-slate-800">{activeLocation}</span>
      </span>
      <select
        aria-label="Active branch"
        value={activeLocationId ?? ""}
        onChange={(event) => setActiveLocation(event.target.value)}
        className="absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0"
      >
        {canViewAll && <option value="all">All branches</option>}
        {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
      </select>
      <ChevronDownIcon className="h-4 w-4 shrink-0 text-slate-400 transition-colors group-hover:text-indigo-600" aria-hidden="true" />
    </label>
  );
}
